<template>
  <div class="gradient-circle" :style="{ width: size + 'px', height: size + 'px' }">
    <svg :width="size" :height="size" class="circle-svg" :viewBox="`0 0 ${size} ${size}`">
      <defs>
        <linearGradient :id="gradientId" x1="0%" y1="50%" x2="100%" y2="50%" gradientUnits="userSpaceOnUse">
          <stop
            v-for="(color, index) in gradientColors"
            :key="index"
            :offset="`${(index / (gradientColors.length - 1)) * 100}%`"
            :stop-color="color"
          />
        </linearGradient>
      </defs>
      <!-- 背景圆环 -->
      <circle
        :cx="center"
        :cy="center"
        :r="radius"
        fill="none"
        :stroke="backgroundColor"
        :stroke-width="strokeWidth"
        stroke-linecap="round"
      />
      <!-- 进度圆环 - 使用path绘制 -->
      <path
        v-if="progress > 0"
        :d="arcPath"
        fill="none"
        :stroke="`url(#${gradientId})`"
        :stroke-width="strokeWidth"
        stroke-linecap="round"
        class="progress-circle"
      />
      <!-- 中心文字 -->
      <g v-if="showText">
        <text
          :x="center"
          :y="center"
          class="progress-text"
          text-anchor="middle"
          dominant-baseline="middle"
        >
          <tspan class="progress-number" :style="{ fontSize: numberFontSize + 'px', fill: textColor }">{{ progressNumber }}</tspan>
          <tspan class="progress-unit" :style="{ fontSize: unitFontSize + 'px', fill: textColor }">%</tspan>
        </text>
      </g>
    </svg>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  // 圆环大小
  size: {
    type: Number,
    default: 100
  },
  // 线条宽度
  strokeWidth: {
    type: Number,
    default: 8
  },
  // 渐变颜色数组
  gradientColors: {
    type: Array,
    default: () => ['#DEF8B3', '#8DD3F5']
  },
  // 进度百分比 (0-100)
  progress: {
    type: Number,
    default: 0,
    validator: (value) => value >= 0 && value <= 100
  },
  // 背景圆环颜色
  backgroundColor: {
    type: String,
    default: '#EFF4FB'
  },
  // 起点位置角度 (0-360, 0为顶部, 顺时针)
  startAngle: {
    type: Number,
    default: 0,
    validator: (value) => value >= 0 && value <= 360
  },
  // 是否显示中心文字
  showText: {
    type: Boolean,
    default: false
  },
  // 文字颜色
  textColor: {
    type: String,
    default: '#000'
  },
  // 数字字体大小
  numberFontSize: {
    type: Number,
    default: 12
  },
  // 单位字体大小
  unitFontSize: {
    type: Number,
    default: 10
  }
})

// 生成唯一的渐变ID
const gradientId = computed(() => `gradient-${Math.random().toString(36).substr(2, 9)}`)

// 圆心位置
const center = computed(() => props.size / 2)

// 圆环半径
const radius = computed(() => (props.size - props.strokeWidth) / 2)

// 进度数字（取整）
const progressNumber = computed(() => Math.round(props.progress))

// 根据角度和半径计算坐标点
const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians)
  }
}

// 生成圆弧路径
const arcPath = computed(() => {
  const progressValue = Math.min(100, Math.max(0, props.progress))
  if (progressValue === 0) return ''

  // 当进度为 100% 时，绘制两个半圆避免起点终点重合
  if (progressValue >= 99.9) {
    const startDegree = props.startAngle
    const midDegree = props.startAngle + 180
    const endDegree = props.startAngle + 359.9 // 使用 359.9 而不是 360，避免完全重合

    const start = polarToCartesian(center.value, center.value, radius.value, startDegree)
    const mid = polarToCartesian(center.value, center.value, radius.value, midDegree)
    const end = polarToCartesian(center.value, center.value, radius.value, endDegree)

    // 绘制两个半圆
    const path = [
      'M',
      start.x,
      start.y,
      'A',
      radius.value,
      radius.value,
      0,
      1, // 大弧
      1, // 顺时针方向
      mid.x,
      mid.y,
      'A',
      radius.value,
      radius.value,
      0,
      1, // 大弧
      1, // 顺时针方向
      end.x,
      end.y
    ].join(' ')

    return path
  }

  const startDegree = props.startAngle
  const endDegree = props.startAngle + (360 * progressValue) / 100

  const start = polarToCartesian(center.value, center.value, radius.value, startDegree)
  const end = polarToCartesian(center.value, center.value, radius.value, endDegree)

  // 判断是否为大弧
  const largeArcFlag = progressValue > 50 ? 1 : 0

  // 构建SVG路径
  const path = [
    'M',
    start.x,
    start.y,
    'A',
    radius.value,
    radius.value,
    0,
    largeArcFlag,
    1, // 顺时针方向
    end.x,
    end.y
  ].join(' ')

  return path
})
</script>

<style scoped lang="scss">
.gradient-circle {
  display: inline-block;
  cursor: pointer;
}

.circle-svg {
  transform: rotate(0deg);
}

.progress-circle {
  transition: d 0.3s ease;
}

.progress-text {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-weight: normal;
  user-select: none;
}

.progress-number {
  font-weight: 500;
}

.progress-unit {
  font-weight: normal;
}
</style>
